/**
 * Color math — mirror of backend/app/services/branding/derive.py.
 *
 * Sprint TENANT-BRAND-1. Keep byte-for-byte parity with the Python
 * helpers: the admin UI's live contrast warnings and the server's
 * fail-stop validation must agree on every pixel.
 */

const HEX_RE = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export function normalizeHex(value: string): string {
  const m = HEX_RE.exec(value.trim());
  if (!m || !m[1]) throw new Error(`invalid hex color: ${value}`);
  let h = m[1].toLowerCase();
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  return `#${h}`;
}

export function hexToRgb(value: string): [number, number, number] {
  const h = normalizeHex(value).slice(1);
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

export function rgbToHex(r: number, g: number, b: number): string {
  const c = (n: number) =>
    Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

export function hexToHsl(value: string): [number, number, number] {
  const rgb = hexToRgb(value);
  const r = rgb[0] / 255;
  const g = rgb[1] / 255;
  const b = rgb[2] / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return [(h * 360) % 360, s * 100, l * 100];
}

function hueToRgb(p: number, q: number, t: number): number {
  if (t < 0) t += 1;
  if (t > 1) t -= 1;
  if (t < 1 / 6) return p + (q - p) * 6 * t;
  if (t < 1 / 2) return q;
  if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
  return p;
}

export function hslToHex(h: number, s: number, l: number): string {
  const hN = ((h % 360) + 360) / 360 % 1;
  const sC = Math.max(0, Math.min(100, s)) / 100;
  const lC = Math.max(0, Math.min(100, l)) / 100;
  if (sC === 0) {
    const v = Math.round(lC * 255);
    return rgbToHex(v, v, v);
  }
  const q = lC < 0.5 ? lC * (1 + sC) : lC + sC - lC * sC;
  const p = 2 * lC - q;
  const r = hueToRgb(p, q, hN + 1 / 3);
  const g = hueToRgb(p, q, hN);
  const b = hueToRgb(p, q, hN - 1 / 3);
  return rgbToHex(r * 255, g * 255, b * 255);
}

export type Mode = 'light' | 'dark';

export function deriveHover(primaryHex: string, mode: Mode): string {
  const [h, s, l] = hexToHsl(primaryHex);
  const newL = mode === 'light' ? l + 8 : l - 8;
  return hslToHex(h, s, Math.max(0, Math.min(100, newL)));
}

export function deriveLight(primaryHex: string, mode: Mode): string {
  const [r, g, b] = hexToRgb(primaryHex);
  const alpha = mode === 'dark' ? 0.18 : 0.12;
  return `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(2)})`;
}

// ── WCAG luminance + contrast ratio ────────────────────────────────────

function channel(c: number): number {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

export function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

export function contrastRatio(fgHex: string, bgHex: string): number {
  const l1 = relativeLuminance(fgHex);
  const l2 = relativeLuminance(bgHex);
  const [lighter, darker] = l1 >= l2 ? [l1, l2] : [l2, l1];
  return (lighter + 0.05) / (darker + 0.05);
}

// ── Readable text (UI-CONTRAST-BRAND) ──────────────────────────────────
// Owner 2026-10-23: text adapts to the background it sits on; brand,
// status and tenant background colours never change.

export const AA_TEXT = 4.5;
const DARK_TEXT = '#0d1117';
const LIGHT_TEXT = '#ffffff';
// Last resort for mid-luminance backgrounds where neither of the above passes.
const BLACK_TEXT = '#000000';
// Luminance where black and white text reach equal contrast.
const MID_LUMINANCE = 0.179;

const RGBA_RE = /^rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:[,\s/]+([\d.]+))?\s*\)$/;

/** ``#rrggbb`` for ``color`` (hex or rgb/rgba()), alpha-composited onto ``underHex``. */
export function flatten(color: string, underHex: string): string {
  const m = RGBA_RE.exec(color.trim());
  if (!m) return normalizeHex(color);
  const alpha = m[4] === undefined ? 1 : Number(m[4]);
  const under = hexToRgb(underHex);
  const mix = (i: number) => Number(m[i + 1]) * alpha + (under[i] ?? 0) * (1 - alpha);
  return rgbToHex(mix(0), mix(1), mix(2));
}

function worstRatio(fgHex: string, bgHexes: readonly string[]): number {
  return Math.min(...bgHexes.map((bg) => contrastRatio(fgHex, bg)));
}

/** White, near-black or black text: the first that meets ``minRatio`` on
 * every background; otherwise whichever comes closest. */
export function onColor(bgHexes: string | readonly string[], minRatio = AA_TEXT): string {
  const bgs = typeof bgHexes === 'string' ? [bgHexes] : bgHexes;
  const candidates = [LIGHT_TEXT, DARK_TEXT, BLACK_TEXT];
  return candidates.find((text) => worstRatio(text, bgs) >= minRatio)
    ?? candidates.reduce((best, text) => (worstRatio(text, bgs) > worstRatio(best, bgs) ? text : best));
}

/** ``fgHex`` with its hue kept, darkened on light backgrounds or
 * lightened on dark ones until it meets ``minRatio`` on every background. */
export function readableOn(fgHex: string, bgHexes: string | readonly string[], minRatio = AA_TEXT): string {
  const bgs = typeof bgHexes === 'string' ? [bgHexes] : bgHexes;
  const fg = normalizeHex(fgHex);
  if (worstRatio(fg, bgs) >= minRatio) return fg;
  const darken = bgs.reduce((sum, bg) => sum + relativeLuminance(bg), 0) / bgs.length > MID_LUMINANCE;
  const [h, s, l] = hexToHsl(fg);
  for (let step = 1; step <= 100; step++) {
    const lightness = darken ? l - step : l + step;
    if (lightness < 0 || lightness > 100) break;
    const candidate = hslToHex(h, s, lightness);
    if (worstRatio(candidate, bgs) >= minRatio) return candidate;
  }
  return darken ? DARK_TEXT : LIGHT_TEXT;
}

// ── Palette types + WCAG AA validator ──────────────────────────────────

export interface PaletteMode {
  brand_primary: string | null;
  brand_primary_hover: string | null;
  brand_primary_light: string | null;
  color_bg: string | null;
  color_surface: string | null;
  color_border: string | null;
  color_text: string | null;
  color_text_secondary: string | null;
}

export interface Palette {
  light: PaletteMode;
  dark: PaletteMode;
}

export const EMPTY_PALETTE_MODE: PaletteMode = {
  brand_primary: null,
  brand_primary_hover: null,
  brand_primary_light: null,
  color_bg: null,
  color_surface: null,
  color_border: null,
  color_text: null,
  color_text_secondary: null,
};

export const EMPTY_PALETTE: Palette = {
  light: { ...EMPTY_PALETTE_MODE },
  dark: { ...EMPTY_PALETTE_MODE },
};

// Must mirror PLATFORM_DEFAULTS in backend/app/services/branding/contrast.py
export const PLATFORM_DEFAULTS: Record<Mode, Record<keyof PaletteMode, string>> = {
  light: {
    brand_primary: '#2563eb',
    brand_primary_hover: '#1d4ed8',
    brand_primary_light: '#dbeafe',
    color_bg: '#ffffff',
    color_surface: '#f8fafc',
    color_border: '#e2e8f0',
    color_text: '#1e293b',
    color_text_secondary: '#515e71',
  },
  dark: {
    brand_primary: '#3b82f6',
    brand_primary_hover: '#60a5fa',
    brand_primary_light: 'rgba(59, 130, 246, 0.18)',
    color_bg: '#0d1117',
    color_surface: '#161b22',
    color_border: '#30363d',
    color_text: '#e6edf3',
    color_text_secondary: '#a4afbe',
  },
};

export function effectiveColor(
  palette: Palette | null,
  mode: Mode,
  token: keyof PaletteMode,
): string {
  const tenantValue = palette?.[mode]?.[token];
  if (tenantValue) return tenantValue;
  // Auto-derive hover + light if primary is set but the derived token isn't.
  if (token === 'brand_primary_hover') {
    const primary = palette?.[mode]?.brand_primary;
    if (primary) return deriveHover(primary, mode);
  }
  if (token === 'brand_primary_light') {
    const primary = palette?.[mode]?.brand_primary;
    if (primary) return deriveLight(primary, mode);
  }
  return PLATFORM_DEFAULTS[mode][token];
}

export interface ContrastFailure {
  mode: Mode;
  pair: string;
  fg_token: PairToken;
  bg_token: keyof PaletteMode;
  fg_hex: string;
  bg_hex: string;
  ratio: number;
  required: number;
}

// Muted text isn't brandable (tokens.css --color-text-muted); a tenant
// background must still carry it. Mirrors contrast.py.
export const PLATFORM_MUTED: Record<Mode, string> = { light: '#607086', dark: '#7e8da2' };
type PairToken = keyof PaletteMode | 'color_text_muted';

const PAIRS: Array<[string, PairToken, keyof PaletteMode, number]> = [
  ['body text on background', 'color_text', 'color_bg', 4.5],
  ['body text on surface', 'color_text', 'color_surface', 4.5],
  ['secondary text on background', 'color_text_secondary', 'color_bg', 4.5],
  ['secondary text on surface', 'color_text_secondary', 'color_surface', 4.5],
  ['muted text on background', 'color_text_muted', 'color_bg', 4.5],
  ['muted text on surface', 'color_text_muted', 'color_surface', 4.5],
  ['link text on background', 'brand_primary', 'color_bg', 4.5],
  ['active nav on surface', 'brand_primary', 'color_surface', 3.0],
];

function isHex6(value: string): boolean {
  return /^#[0-9a-fA-F]{6}$/.test(value);
}

/** Text colours derived from the effective brand palette for ``mode``:
 * ``onPrimary`` for text on brand-primary (and hover) backgrounds,
 * ``primaryText`` for brand-coloured text on the page, surfaces and the
 * brand tint (active nav, selected rows). Mirrors tokens.css defaults. */
export function deriveBrandText(palette: Palette | null, mode: Mode): { onPrimary: string; primaryText: string } {
  const color = (token: keyof PaletteMode) => effectiveColor(palette, mode, token);
  const bg = color('color_bg');
  const surface = color('color_surface');
  const tint = color('brand_primary_light');
  const primary = color('brand_primary');
  const onBoth = onColor([primary, color('brand_primary_hover')]);
  return {
    // Passes on the button and its hover; failing that, on the resting button.
    onPrimary: contrastRatio(onBoth, primary) >= AA_TEXT && contrastRatio(onBoth, color('brand_primary_hover')) >= AA_TEXT
      ? onBoth : onColor(primary),
    primaryText: readableOn(primary, [bg, surface, flatten(tint, bg), flatten(tint, surface)]),
  };
}

export function validatePalette(palette: Palette | null): ContrastFailure[] {
  const failures: ContrastFailure[] = [];
  for (const mode of ['light', 'dark'] as const) {
    for (const [pair, fgToken, bgToken, required] of PAIRS) {
      const fg = fgToken === 'color_text_muted' ? PLATFORM_MUTED[mode] : effectiveColor(palette, mode, fgToken);
      const bg = effectiveColor(palette, mode, bgToken);
      // Skip when either side isn't a hex6 (mid-edit, rgba, etc.) — the
      // server-side fail-stop catches anything that survives Save.
      if (!isHex6(fg) || !isHex6(bg)) continue;
      const ratio = Math.round(contrastRatio(fg, bg) * 100) / 100;
      if (ratio < required) {
        failures.push({
          mode, pair, fg_token: fgToken, bg_token: bgToken,
          fg_hex: fg, bg_hex: bg, ratio, required,
        });
      }
    }
  }
  return failures;
}
